import { useState, useRef, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Reveal, SectionTag, SectionTitle } from "./shared";
import { SeventhParkview } from "./SeventhParkview";
import { YeidaSectorPanel } from "./YeidaSectorPanel";
import { ProjectCard } from "./ProjectCard";
import { YEIDA_SECTORS, ACE_PROJECTS, PURVANCHAL_PROJECTS } from "../data/site";

const TAB_TRIGGER_CLASS =
  "shrink-0 px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] rounded-full border border-[#D4AF37]/20 text-[#71717A] data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C5A059] data-[state=active]:to-[#D4AF37] data-[state=active]:text-black data-[state=active]:border-transparent data-[state=active]:shadow-none transition-colors";

export function CorridorExplorer({ onEnquire7pv, onEnquireYeidaSector, onEnquireAce, onEnquirePurvanchal }) {
  const [tab, setTab] = useState("7pv");
  const panelRef = useRef(null);

  const changeTab = (val) => {
    setTab(val);
    requestAnimationFrame(() => panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  useEffect(() => {
    const handler = (e) => {
      setTab(e.detail);
      requestAnimationFrame(() => {
        if (window.__lenis) window.__lenis.scrollTo(panelRef.current, { offset: -90, duration: 1.6 });
        else panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    };
    window.addEventListener("corridor:open-tab", handler);
    return () => window.removeEventListener("corridor:open-tab", handler);
  }, []);

  return (
    <section id="explore-corridor" className="relative py-28 lg:py-40 bg-[#0D0D0D] overflow-hidden" data-testid="corridor-explorer-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionTag>Explore the Corridor</SectionTag>
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-end mb-10">
          <SectionTitle>
            Beyond the Township.<br />
            <span className="text-gold-gradient italic">The Full Corridor.</span>
          </SectionTitle>
          <Reveal delay={0.15}>
            <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed max-w-lg">
              From 7th Park View inside GYC, to YEIDA&rsquo;s own sectors, to the Ace and Purvanchal
              developments nearby &mdash; here is every part of the Yamuna Expressway growth story
              worth knowing about.
            </p>
          </Reveal>
        </div>

        <Tabs value={tab} onValueChange={changeTab}>
          <div className="relative mb-12">
            <TabsList className="w-full max-w-full flex h-auto flex-nowrap overflow-x-auto justify-start gap-2.5 bg-transparent p-0 -mx-1 px-1" data-testid="corridor-explorer-tabs">
              <TabsTrigger value="7pv" className={TAB_TRIGGER_CLASS} data-testid="corridor-tab-7pv">7th Park View</TabsTrigger>
              {YEIDA_SECTORS.map((s) => (
                <TabsTrigger key={s.id} value={s.id} className={TAB_TRIGGER_CLASS} data-testid={`corridor-tab-${s.id}`}>{s.sector}</TabsTrigger>
              ))}
              <TabsTrigger value="ace" className={TAB_TRIGGER_CLASS} data-testid="corridor-tab-ace">Ace &middot; Sector 22D</TabsTrigger>
              <TabsTrigger value="purvanchal" className={TAB_TRIGGER_CLASS} data-testid="corridor-tab-purvanchal">Purvanchal &amp; More</TabsTrigger>
            </TabsList>
            <div className="lg:hidden pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-[#0D0D0D] to-transparent" />
          </div>

          <div ref={panelRef} />

          <TabsContent value="7pv" className="mt-0">
            <SeventhParkview onEnquire={onEnquire7pv} />
          </TabsContent>

          {YEIDA_SECTORS.map((s) => (
            <TabsContent key={s.id} value={s.id} className="mt-0">
              <YeidaSectorPanel data={s} onEnquire={onEnquireYeidaSector} />
            </TabsContent>
          ))}

          <TabsContent value="ace" className="mt-0">
            <Reveal delay={0.05}>
              <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-2xl mb-8">
                The Ace Group runs a cluster of residential, plotted and commercial developments
                across Sectors 22A and 22D on the Yamuna Expressway. Note: &ldquo;Ace Arte&rdquo; is
                actually located in Sector 150, Noida, not on the Yamuna Expressway corridor &mdash;
                the projects below are Ace&rsquo;s genuine Yamuna Expressway portfolio.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ace-projects-grid">
              {ACE_PROJECTS.map((p, i) => (
                <ProjectCard key={p.id} project={p} onEnquire={onEnquireAce} delay={0.05 + i * 0.05} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="purvanchal" className="mt-0">
            <Reveal delay={0.05}>
              <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-2xl mb-8">
                Purvanchal Group and Eldeco are two of the other established developers building in
                Sector 22D, alongside Gaur and Ace.
              </p>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-6" data-testid="purvanchal-projects-grid">
              {PURVANCHAL_PROJECTS.map((p, i) => (
                <ProjectCard key={p.id} project={p} onEnquire={onEnquirePurvanchal} delay={0.05 + i * 0.05} />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <p className="mt-10 text-[11px] text-[#52525b] leading-relaxed max-w-4xl" data-testid="corridor-explorer-disclaimer">
          Ace, Purvanchal and Eldeco projects listed here are independent developments by their
          respective developers and are not part of Gaur Yamuna City. Facts are compiled from each
          developer&rsquo;s published information as of research date - verify RERA registration,
          price and possession status directly before any transaction.
        </p>
      </div>
    </section>
  );
}
